import type { LevelDefinition } from './Level.ts';

/**
 * Level 9 — the goal is not the target.
 *
 * The hardest thing to unlearn in a game about steering fluid is that the fluid is the
 * point. Here it is a tool, and the level says so by letting the obvious move succeed
 * completely at the wrong task: put an attractor on the goal and the blob pours through
 * the hatch and fills it, beautifully, while the ball creeps the other way along the shelf
 * and the run ends at zero per cent. A goal full of ferrofluid and a loss.
 *
 * The ball has to be walked left along the shelf to the hatch before anything falls
 * through it, and the only way to push it left is to throw the fluid *right* first, so it
 * packs against the far wall and comes back at the ball from behind.
 *
 * Proven headlessly: the intended pair solves in 8.0s with the ball tracking 700 -> 661 ->
 * 586 -> 551 along the shelf before it drops; an attractor on the goal settles at 0% with
 * the fluid at (534, 1148) — inside the goal — and the ball moved the wrong way; an
 * attractor on the ball settles in 3.6s having barely moved it; and no single magnet
 * solves it from any of 270 grid positions.
 */
export const level09: LevelDefinition = {
  id: 'level09',
  name: 'Down the hatch',
  hint: 'The fluid can drop through on its own. The ball has to be walked to the hatch first.',
  magnetsAvailable: 2,
  fluid: {
    amount: 380,
    source: { x: 700, y: 430 },
    radius: 44,
  },
  ball: { x: 700, y: 845, radius: 40 },
  goal: { x: 510, y: 1180, radius: 105, type: 'ball' },
  walls: [
    // Arena.
    { type: 'segment', x1: 120, y1: 240, x2: 880, y2: 240, thickness: 26 },
    { type: 'segment', x1: 880, y1: 240, x2: 880, y2: 1400, thickness: 26 },
    { type: 'segment', x1: 880, y1: 1400, x2: 120, y2: 1400, thickness: 26 },
    { type: 'segment', x1: 120, y1: 1400, x2: 120, y2: 240, thickness: 26 },

    // The shelf the ball rests on, with the hatch it has to be walked to.
    { type: 'segment', x1: 120, y1: 900, x2: 420, y2: 900, thickness: 26 },
    { type: 'segment', x1: 600, y1: 900, x2: 880, y2: 900, thickness: 26 },
  ],
};
